import React, { memo, useContext, useMemo } from 'react';
import { batch, useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  addEventValuesAction,
  addNewRowAction,
  convertVirtualToBasicEventAction,
  IRow,
  IRowMeta,
  removeErrorAction,
  RowType,
} from '../../../../redux/slices/oldBuilder';
import {
  activeSchemaIdSelector,
  builderSelectedOptionsSelector,
  errorPrimitiveSelector,
  eventsPrimitiveSelector,
  rowSelector,
  stepsSelector,
} from '../../../../redux/selectors/builder';
import {
  FunnelOrderKey,
  PermissionKey,
  QueryType,
  TagOption,
} from '../../../../utils/apollo/graphqlSchema';
import { compact } from 'lodash';
import { isEmptyEventConditions } from '../../../../utils/builder';
import { MAX_STEP_COUNT } from '../../../../config/constants';
import { canExpandVirtualEvent } from '../../../../redux/slices/oldBuilder/utils';
import EntitySelection from '../../EntitySelection';
import {
  createBasicEventHandler,
  createTagOptionInput,
  removeEntityFromSelection,
  toggleEntityInSelection,
} from '../../EntitySelection/utils';
import {
  IEntitySelectionLabelProps,
  IExpandVirtualEventCallbackFactory,
  IRemoveEntityHandler,
  ISelectEntityHandler,
} from '../../EntitySelection/types';
import { assertCondition } from '../../../../utils/types';
import { useAppSelector } from '../../../../redux/hooks';
import usePermission from '../../../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../../../pages/PermissionControls/PermissionElementWrapper';
import { StepEventMenuOpenContext } from '../../../../utils/contexts/StepEventMenuOpenContext';
import { useBootstrapSelector } from '../../../../redux/selectorHooks';
import { IEventEntitySelection } from './types';
import DndDroppableZone from '../../../DndEntity/DndDroppableZone';
import { EntityDropzoneType } from '../../../QuickAccessDictionary/DictionaryTabs/types';
import {
  createDictionaryEntityDropzoneId,
  getDndActiveItemData,
} from '../../../DndEntity/utils';
import {
  EntityDropzoneDataTypeEnum,
  IEventEntityDropzoneDataType,
} from '../../../DndEntity/validation';
import { useDndEntityInternalContext } from '../../../DndEntity';
import {
  checkEventTypeError,
  getBuilderEventsInputCustomMessage,
} from './utils';

interface IProps {
  rowMeta: IRowMeta;
  placeholder?: string;
  onOpen?: () => void;
}

const DropdownContainer = styled.div`
  max-width: 480px;
`;

const getEventTags = ({
  hasAnyEvent,
  hasBasicEvent,
  hasVirtualEvent,
}: {
  hasAnyEvent: boolean;
  hasBasicEvent: boolean;
  hasVirtualEvent: boolean;
}) => {
  const resultTags = [];
  if (hasBasicEvent) {
    resultTags.push(createTagOptionInput(TagOption.BasicEvent));
  }
  if (hasVirtualEvent) {
    resultTags.push(createTagOptionInput(TagOption.VirtualEvent));
  }
  if (hasAnyEvent) {
    resultTags.push(createTagOptionInput(TagOption.AnyEvent));
  }
  return resultTags;
};

export default memo(({ rowMeta, placeholder, onOpen }: IProps) => {
  const dispatch = useDispatch();
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const schemaConfig = useBootstrapSelector('schemaConfig');
  const { checkIsPermitted } = usePermission();
  const isPermittedToViewVirtualEvent = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.VirtualEventRead],
    schemaId: activeSchemaId,
  });
  const isPermittedToViewBasicEvent = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.EventRead],
    schemaId: activeSchemaId,
  });
  const { rowIndex, rowType, negativeStepRowIndex } = rowMeta;
  const steps = useAppSelector(stepsSelector);
  const eventsSelector = useMemo(eventsPrimitiveSelector, []);
  const [basicEvents, virtualEvents, anyEvents, anyTableEvents] =
    useAppSelector((state) => eventsSelector(state, rowMeta));
  const { setIsStepEventMenuOpen } = useContext(StepEventMenuOpenContext);

  const { activeItem } = useDndEntityInternalContext();

  const selectedOptions = useAppSelector((state) =>
    builderSelectedOptionsSelector(state),
  );

  const stepOrders = useAppSelector((state) => state.builder.stepOrders);

  const errorSelector = useMemo(errorPrimitiveSelector, []);
  const errorKey =
    typeof negativeStepRowIndex === 'number' ? 'negativeSteps' : 'events';

  const errors = useAppSelector((state) =>
    errorSelector(state, rowMeta, errorKey),
  );

  const compactedEvents = compact([
    anyEvents ? anyEvents[0] : null,
    ...anyTableEvents,
    ...basicEvents,
    ...virtualEvents,
  ]) as IEventEntitySelection[];

  // Hack: remove any in path, change it after add retention to rowMeta
  const removeAnyInPath =
    rowType === RowType.foldEvents ||
    (rowType === RowType.paths &&
      ((rowIndex === 0 && rowMeta.pathDirection === 'FORWARD') || // can't start forward with Any
        (rowIndex === 1 && rowMeta.pathDirection === 'BACKWARD'))); // can't end backward with Any

  const removeAnyInSteps =
    rowType === RowType.steps &&
    selectedOptions?.type === QueryType.Funnel &&
    !(
      // Any is allowed in funnel in the last step if Funnel Step Order is Sequence.
      // If Selected Order is Custom, we need to check the stepOrder from the last row.
      (
        rowMeta.isLastStep &&
        rowIndex > 0 &&
        (selectedOptions.funnelOrder === FunnelOrderKey.Sequence ||
          (selectedOptions.funnelOrder === FunnelOrderKey.Custom &&
            stepOrders?.[rowMeta.rowIndex - 1] === FunnelOrderKey.Sequence))
      )
    );

  const activeSchemaSupportsAnyEvent = useMemo(
    () =>
      schemaConfig?.schemas.find((s) => s.id === activeSchemaId)
        ?.supportsAnyEvent ?? false,
    [activeSchemaId, schemaConfig?.schemas],
  );

  const tags = getEventTags({
    hasAnyEvent:
      activeSchemaSupportsAnyEvent && !removeAnyInPath && !removeAnyInSteps,
    hasBasicEvent: isPermittedToViewBasicEvent,
    hasVirtualEvent: isPermittedToViewVirtualEvent,
  });

  const selectEventHandler: ISelectEntityHandler = (entity) => {
    assertCondition(
      entity.__typename === 'VirtualEvent' ||
        entity.__typename === 'BasicEventValue' ||
        entity.__typename === 'AnyEvent' ||
        entity.__typename === 'AnyTableEvent',
    );

    // any event and virtual event always replace the entire selection
    let currentSelection: IEventEntitySelection[];
    const hasVirtualEvent = compactedEvents.some(
      (e) => e.__typename === 'VirtualEvent',
    );
    const hasAnyEvent = compactedEvents.some(
      (e) => e.__typename === 'AnyEvent',
    );
    if (
      hasVirtualEvent ||
      hasAnyEvent ||
      entity.__typename === 'VirtualEvent' ||
      entity.__typename === 'AnyEvent'
    ) {
      currentSelection = [];
    } else {
      // if neither "Any" or virtual event, retain the selection
      currentSelection = [...compactedEvents];
    }
    const newSelection = toggleEntityInSelection(currentSelection, entity);
    updateBuilder(newSelection);
  };

  const removeEventHandler: IRemoveEntityHandler = (entity) => {
    assertCondition(
      entity.__typename === 'VirtualEvent' ||
        entity.__typename === 'BasicEventValue' ||
        entity.__typename === 'AnyEvent' ||
        entity.__typename === 'AnyTableEvent',
    );
    updateBuilder(removeEntityFromSelection(compactedEvents, entity));
  };

  const updateBuilder = (newEvents: IEventEntitySelection[]) => {
    // Step auto add if last step is not empty and current action is adding event from 0 to 1
    const isLastStepRowEmpty = isEmptyEventConditions(
      steps[steps.length - 1].eventConditions,
    );

    if (
      rowType === RowType.steps &&
      newEvents.length > 0 &&
      // if the last step is not empty
      // or current row is the last step, no need to check the last step
      (!isLastStepRowEmpty || steps.length - 1 === rowIndex) &&
      // total size check
      steps.length < MAX_STEP_COUNT
    ) {
      dispatch(
        addNewRowAction({
          rowMeta: { rowType, rowIndex: steps.length - 1 },
        }),
      );
    }
    batch(() => {
      dispatch(removeErrorAction({ rowMeta, errorKey }));
      dispatch(
        addEventValuesAction({
          rowMeta,
          newEvents,
        }),
      );
    });
  };

  const eventLabelProps = useMemo((): IEntitySelectionLabelProps[] => {
    const virtualEventExpandHandler: IExpandVirtualEventCallbackFactory = (
      virtualEvent,
    ) => {
      if (canExpandVirtualEvent(virtualEvent, rowMeta)) {
        return () => {
          dispatch(
            convertVirtualToBasicEventAction({
              rowMeta,
              virtualEventId: virtualEvent.id,
            }),
          );
        };
      } else {
        return undefined;
      }
    };
    return [
      {
        type: 'VirtualEvent',
        onExpandFactory: virtualEventExpandHandler,
      },
    ];
  }, [dispatch, rowMeta]);

  const handleOpen = () => {
    onOpen?.();
    if (setIsStepEventMenuOpen) {
      setIsStepEventMenuOpen(true);
    }
  };

  const handleClose = () => {
    if (setIsStepEventMenuOpen) {
      setIsStepEventMenuOpen(false);
    }
  };

  const currentRow: IRow = useAppSelector((state) =>
    rowSelector(state, rowMeta),
  );

  const eventsDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Events,
    rowMeta,
  );

  const checkHasErrors = () => {
    const data = getDndActiveItemData(activeItem);

    if (data) {
      if (data.__typename === 'TagLeafBasicEvent') {
        return checkEventTypeError({
          typename: 'BasicEventValue',
          keyOrId: data.key,
          events: compactedEvents,
        });
      }

      if (data.__typename === 'TagLeafVirtualEvent') {
        // only allow 1 virtual event in the events input
        if (compactedEvents.length > 0) {
          return true;
        }

        return checkEventTypeError({
          typename: 'VirtualEvent',
          keyOrId: data.id,
          events: compactedEvents,
        });
      }
    }

    return false;
  };

  return (
    <DropdownContainer>
      {/* checking generated permitted tags only */}
      <PermissionElementWrapper isPermitted={tags.length > 0}>
        <DndDroppableZone
          accepts={[EntityDropzoneType.Events]}
          id={eventsDropzoneId}
          data={
            {
              id: eventsDropzoneId,
              type: EntityDropzoneDataTypeEnum.Events,
              existingEvents: compactedEvents,
              rowMeta,
              shouldAddMeasureFunction: !currentRow?.fn?.key,
              errorKey,
            } satisfies IEventEntityDropzoneDataType
          }
          hasError={checkHasErrors()}
          customMessage={getBuilderEventsInputCustomMessage({
            activeItem,
            events: compactedEvents,
          })}
        >
          <EntitySelection
            placeholder={placeholder}
            isDisabled={tags.length === 0}
            schemaId={activeSchemaId}
            selectedEntities={compactedEvents}
            tags={tags}
            onSelectEntity={selectEventHandler}
            onRemoveEntity={removeEventHandler}
            onCreateEntity={createBasicEventHandler}
            onOpen={handleOpen}
            onClose={handleClose}
            labelProps={eventLabelProps}
            isError={errors}
          />
        </DndDroppableZone>
      </PermissionElementWrapper>
    </DropdownContainer>
  );
});
